import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, CloudUpload, Loader2, Mail, MessageCircle, Phone } from 'lucide-react';
import type {
  PublicQuestionnaire,
  PublicQuestionnaireQuestion,
  QuestionnaireAnswerValue,
} from '@peles/shared';
import {
  getPublicQuestionnaire,
  savePublicAnswer,
  searchPublicVendors,
  submitPublicQuestionnaire,
} from '../api/publicQuestionnaire';
import { ProducerBrandingPanel, QuestionInput } from '../components/questionnaires';

interface LocalAnswer {
  value: QuestionnaireAnswerValue;
  vendorId: string | null;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function getClientTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * Public no-login questionnaire fill page (/questionnaire/:token). Answers
 * autosave per question; linked questions arrive prefilled from event data.
 */
export function PublicQuestionnairePage() {
  const { token } = useParams<{ token: string }>();
  const [questionnaire, setQuestionnaire] = useState<PublicQuestionnaire | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<Record<string, LocalAnswer>>({});
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [submitting, setSubmitting] = useState(false);
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const clientTimezone = useMemo(getClientTimezone, []);

  useEffect(() => {
    if (!token) return;
    getPublicQuestionnaire(token)
      .then((data) => {
        setQuestionnaire(data);
        const initial: Record<string, LocalAnswer> = {};
        for (const section of data.sections) {
          for (const question of section.questions) {
            initial[question.id] = {
              value: question.answer?.value ?? {},
              vendorId: question.answer?.vendorId ?? null,
            };
          }
        }
        setAnswers(initial);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Failed to load questionnaire'),
      )
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const timers = saveTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  const isLocked = questionnaire?.status === 'SUBMITTED' || questionnaire?.status === 'REVIEWED';

  const handleChange = useCallback(
    (
      question: PublicQuestionnaireQuestion,
      value: QuestionnaireAnswerValue,
      vendorId?: string | null,
    ) => {
      if (!token || isLocked || submitting) return;
      const next: LocalAnswer = { value, vendorId: vendorId !== undefined ? vendorId : null };
      setAnswers((prev) => ({ ...prev, [question.id]: next }));
      setSaveState('saving');
      if (saveTimers.current[question.id]) clearTimeout(saveTimers.current[question.id]);
      saveTimers.current[question.id] = setTimeout(() => {
        delete saveTimers.current[question.id];
        savePublicAnswer(token, question.id, {
          value: next.value,
          vendorId: next.vendorId,
          clientTimezone,
        })
          .then(() => setSaveState('saved'))
          .catch(() => setSaveState('error'));
      }, 700);
    },
    [token, isLocked, submitting, clientTimezone],
  );

  const searchVendors = useCallback((query: string) => searchPublicVendors(token!, query), [token]);

  async function handleSubmit() {
    if (!token) return;
    setSubmitting(true);
    try {
      // Flush any pending debounced saves before submitting.
      await Promise.all(
        Object.entries(saveTimers.current).map(([questionId, timer]) => {
          clearTimeout(timer);
          const local = answers[questionId];
          return local
            ? savePublicAnswer(token, questionId, { ...local, clientTimezone }).catch(() => null)
            : Promise.resolve(null);
        }),
      );
      const updated = await submitPublicQuestionnaire(token, clientTimezone);
      // The submit response carries no branding — keep what the initial load resolved.
      setQuestionnaire((prev) => ({ ...updated, branding: prev?.branding ?? updated.branding }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 size={32} className="animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !questionnaire) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Questionnaire Unavailable</h1>
          <p className="text-sm text-gray-600">
            {error ?? 'This link is invalid or has expired. Please contact your event producer.'}
          </p>
        </div>
      </div>
    );
  }

  const branding = questionnaire.branding;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Producer brand hero — logo, name, contact info from producer_vendor_id */}
      {branding && (
        <div
          className="bg-gradient-to-r from-primary-800 via-primary-700 to-primary-600 text-white"
          data-testid="branding-header"
        >
          <div className="max-w-5xl mx-auto px-4 py-4 flex flex-wrap items-center gap-x-4 gap-y-2">
            {branding.logoUrl && (
              <img
                src={branding.logoUrl}
                alt={`${branding.businessName} logo`}
                className="w-11 h-11 rounded-lg object-contain bg-white/95 p-0.5"
              />
            )}
            <span className="text-lg font-bold tracking-tight">{branding.businessName}</span>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-primary-100 sm:ml-auto">
              {branding.phone && (
                <a
                  href={`tel:${branding.phone}`}
                  className="flex items-center gap-1 hover:text-white"
                >
                  <Phone size={13} /> {branding.phone}
                </a>
              )}
              {branding.email && (
                <a
                  href={`mailto:${branding.email}`}
                  className="flex items-center gap-1 hover:text-white"
                >
                  <Mail size={13} /> {branding.email}
                </a>
              )}
              {branding.whatsappUrl && (
                <a
                  href={branding.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500 text-white rounded-full font-medium hover:bg-green-600 transition-colors"
                  data-testid="header-whatsapp-link"
                >
                  <MessageCircle size={13} /> WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900 truncate">{questionnaire.title}</h1>
            {questionnaire.event && (
              <p className="text-xs text-gray-500 truncate">
                {questionnaire.event.name}
                {questionnaire.event.startAt &&
                  ` · ${new Date(questionnaire.event.startAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}`}
                {questionnaire.event.venueName && ` · ${questionnaire.event.venueName}`}
              </p>
            )}
          </div>
          {!isLocked && (
            <span
              className="shrink-0 flex items-center gap-1 text-xs text-gray-400"
              data-testid="save-indicator"
            >
              {saveState === 'saving' && (
                <>
                  <CloudUpload size={14} className="animate-pulse" /> Saving…
                </>
              )}
              {saveState === 'saved' && (
                <>
                  <CheckCircle2 size={14} className="text-green-500" /> Saved
                </>
              )}
              {saveState === 'error' && (
                <span className="text-red-500">Couldn&rsquo;t save — check connection</span>
              )}
            </span>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-6 lg:items-start">
        <div className="space-y-6">
          {isLocked && (
            <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800">
              <CheckCircle2 size={18} />
              Thank you! Your answers were submitted
              {questionnaire.submittedAt &&
                ` on ${new Date(questionnaire.submittedAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}`}
              . Contact your producer if anything needs to change.
            </div>
          )}
          {questionnaire.description && !isLocked && (
            <p className="text-sm text-gray-600">{questionnaire.description}</p>
          )}

          {questionnaire.sections.map((section) => (
            <section key={section.id} className="bg-white border border-gray-200 rounded-xl">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-primary-800">{section.name}</h2>
                {section.description && (
                  <p className="mt-0.5 text-xs text-gray-500">{section.description}</p>
                )}
              </div>
              <div className="divide-y divide-gray-50">
                {section.questions.map((question) => {
                  const local = answers[question.id] ?? { value: {}, vendorId: null };
                  return (
                    <div key={question.id} className="px-4 py-4">
                      <label className="block text-sm text-gray-800 mb-2">
                        {question.prompt}
                        {question.isRequired && <span className="text-red-500 ml-0.5">*</span>}
                      </label>
                      {question.helpText && (
                        <p className="text-xs text-gray-500 -mt-1 mb-2">{question.helpText}</p>
                      )}
                      <QuestionInput
                        questionType={question.questionType}
                        config={question.config}
                        value={local.value}
                        vendorId={local.vendorId}
                        disabled={isLocked || submitting}
                        onChange={(value, vendorId) => handleChange(question, value, vendorId)}
                        searchVendors={searchVendors}
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          ))}

          {/* Marketing panel inline on mobile — the sidebar hides below lg */}
          {branding && (
            <div className="lg:hidden">
              <ProducerBrandingPanel branding={branding} />
            </div>
          )}
        </div>

        {branding && (
          <div className="hidden lg:block sticky top-24">
            <ProducerBrandingPanel branding={branding} />
          </div>
        )}
      </main>

      {!isLocked && (
        <footer className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 z-20">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              Answers save automatically — you can return anytime with this link.
            </p>
            <button
              onClick={() => void handleSubmit()}
              disabled={submitting}
              className="shrink-0 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
              data-testid="submit-questionnaire"
            >
              {submitting ? 'Submitting…' : 'Submit Questionnaire'}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}
